import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, AlertTriangle } from "lucide-react";

import { useTranslation } from "react-i18next";
/**
 * AmountConfirmDialog — re-type-amount double-check before committing a credit
 * entry. Rendered from CSA + Manager + Dealer flows. Refuses to confirm if
 * the re-typed value differs from the intended amount by > ₹0.50.
 *
 * Props:
 *   open          bool
 *   onOpenChange  (bool)=>void
 *   amount        number  — the value being posted
 *   customerName  string
 *   vehicle       string | null
 *   dueDate       string | null (YYYY-MM-DD)
 *   loading       bool
 *   onConfirm     (confirmedAmount: number) => void
 */
export default function AmountConfirmDialog({
  open, onOpenChange, amount, customerName, vehicle, dueDate, loading, onConfirm,
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const inputRef = useRef(null);

  useEffect(() => { if (open) { setTyped(""); setTimeout(() => inputRef.current?.focus(), 80); } }, [open]);

  const numeric = Number(typed);
  const matches = useMemo(() => {
    if (!typed) return false;
    if (Number.isNaN(numeric)) return false;
    return Math.abs(numeric - Number(amount || 0)) < 0.5;
  }, [numeric, typed, amount]);

  const overdueDate = useMemo(() => {
    if (!dueDate) return null;
    try {
      const dt = new Date(dueDate + "T00:00:00");
      return dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch { return dueDate; }
  }, [dueDate]);

  const inWords = useMemo(() => amount ? numberInWordsIndian(Number(amount)) : "", [amount]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!matches || loading) return;
    onConfirm(numeric);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="amount-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-amber-600" />
            Confirm credit entry
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm">
            <div className="text-[11px] font-black uppercase tracking-widest text-amber-800">{t("components.amountconfirmdialog.you_are_posting")}</div>
            <div className="mt-1 font-display text-3xl font-bold text-amber-950">
              ₹ {Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </div>
            {inWords && (
              <div className="mt-1 text-[11px] italic text-amber-900/70">{inWords} only</div>
            )}
            <div className="mt-3 space-y-0.5 text-[12px] text-amber-950/80">
              <div><b>To</b>: {customerName}{vehicle ? ` · ${vehicle}` : ""}</div>
              {overdueDate && <div><b>{t("components.amountconfirmdialog.due")}</b>: {overdueDate}</div>}
            </div>
          </div>

          <div>
            <Label htmlFor="reenter-amount" className="flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
              Re-type the amount to confirm
            </Label>
            <Input
              id="reenter-amount"
              ref={inputRef}
              type="number"
              inputMode="decimal"
              step="0.01"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="e.g. 1250.00"
              className={
                "mt-1 h-11 text-lg font-bold tabular-nums " +
                (typed && !matches ? "border-red-500 ring-2 ring-red-500/30" : "")
              }
              data-testid="amount-confirm-input"
              autoFocus
            />
            {typed && !matches && (
              <div className="mt-1 text-xs font-semibold text-red-600" data-testid="amount-mismatch">
                Amounts don't match. Please re-check both.
              </div>
            )}
            {matches && (
              <div className="mt-1 text-xs font-semibold text-emerald-700" data-testid="amount-match">
                ✓ Matches — safe to post
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!matches || loading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="amount-confirm-submit"
            >
              {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Post credit entry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/**
 * numberInWordsIndian — friendly amount-in-words for the confirmation card.
 * Rupees + paise, Lakh/Crore units.
 */
export function numberInWordsIndian(num) {
  if (!Number.isFinite(num) || num <= 0) return "";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen",
    "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  };
  const three = (n) => {
    const h = Math.floor(n / 100);
    const rest = n % 100;
    let s = "";
    if (h) s += ones[h] + " Hundred";
    if (rest) s += (s ? " " : "") + two(rest);
    return s;
  };
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  if (rupees === 0 && paise === 0) return "Zero Rupees";
  let n = rupees;
  const parts = [];
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;
  if (crore) parts.push(two(crore) + " Crore");
  if (lakh) parts.push(two(lakh) + " Lakh");
  if (thousand) parts.push(two(thousand) + " Thousand");
  if (hundred) parts.push(three(hundred));
  let out = "Rupees " + parts.join(" ").trim();
  if (paise > 0) out += " and " + two(paise) + " Paise";
  return out;
}
